/* Breakz — cookie consent.

   A legal requirement, not a theme one: ekomlov § 3-15 applies from
   01.01.2025 and asks for consent BEFORE any request to a third party,
   not anonymisation afterwards. So there is not a single third-party
   script here, and everything that goes out asks this file first.

   Two stores of record, deliberately:

   1. The `breakz_consent` cookie — our own record. Read synchronously,
      before the Shopify API arrives, and it survives that API failing.
      One year: consent is not perpetual, after a year we ask again.

   2. The Shopify Customer Privacy API — what actually gates Shopify's
      own pixels. Our record alone gates nothing there.

   Disagreement between them is resolved in favour of our record: it is
   timestamped, and it is the one the person saw on screen.

   Shopify's field names are analytics, marketing, preferences,
   sale_of_data. Not analyticsAllowed and not analyticsProcessingAllowed:
   those arrive at a pixel subscriber, which is the other side of the wall.
   The mistake is silent, so the names are collected in WANT below. */

(function () {
  'use strict';

  if (window.BreakzConsent) return;

  var COOKIE = 'breakz_consent';
  var VERSION = 1;
  var YEAR = 365 * 24 * 60 * 60;
  var LOG_KEY = 'breakz.consent.log';
  var LOG_MAX = 25;

  /* Optional categories. `necessary` is not in the list: it cannot be
     switched off, so there is nothing to store. */
  var CATS = ['preferences', 'analytics', 'marketing'];

  var root = document.querySelector('[data-bzc]');
  if (!root) return;

  var bar = root.querySelector('[data-bzc-bar]');
  var modal = root.querySelector('[data-bzc-modal]');
  var ask = root.querySelector('[data-bzc-ask]');
  var askText = root.querySelector('[data-bzc-ask-text]');

  var state = null;
  var pending = null;
  var opener = null;

  /* ── Our own record ─────────────────────────────────────────── */

  function readRecord() {
    var raw = null;
    var parts = document.cookie ? document.cookie.split('; ') : [];

    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(COOKIE + '=') === 0) {
        raw = parts[i].slice(COOKIE.length + 1);
        break;
      }
    }
    if (!raw) return null;

    var rec;
    try {
      rec = JSON.parse(decodeURIComponent(raw));
    } catch (e) {
      return null;
    }

    if (!rec || rec.v !== VERSION || typeof rec.t !== 'number') return null;

    /* A record older than a year counts as non-existent — we ask again.
       The cookie would expire anyway, but it could be restored from a
       profile backup, and then this check is the only defence. */
    if (Math.floor(Date.now() / 1000) - rec.t > YEAR) return null;

    return {
      preferences: !!rec.p,
      analytics: !!rec.a,
      marketing: !!rec.m,
      at: rec.t
    };
  }

  function writeRecord(next, source) {
    next.at = Math.floor(Date.now() / 1000);

    var payload = {
      v: VERSION,
      t: next.at,
      p: next.preferences ? 1 : 0,
      a: next.analytics ? 1 : 0,
      m: next.marketing ? 1 : 0
    };

    document.cookie =
      COOKIE + '=' + encodeURIComponent(JSON.stringify(payload)) +
      '; path=/; max-age=' + YEAR + '; samesite=lax' +
      (location.protocol === 'https:' ? '; secure' : '');

    appendLog(next, source);
  }

  /* A log for brief §11, "consent records stored and exportable".
     For now it is local: server-side records arrive with our app, there
     is nowhere to keep them before that. Read through
     BreakzConsent.records(). */
  function appendLog(next, source) {
    try {
      var log = JSON.parse(localStorage.getItem(LOG_KEY) || '[]');
      if (!Array.isArray(log)) log = [];

      log.push({
        v: VERSION,
        at: new Date(next.at * 1000).toISOString(),
        source: source || 'unknown',
        preferences: !!next.preferences,
        analytics: !!next.analytics,
        marketing: !!next.marketing
      });

      localStorage.setItem(LOG_KEY, JSON.stringify(log.slice(-LOG_MAX)));
    } catch (e) {
      /* Private mode or a full quota. Consent must not fall over because
         of it — the cookie is already written. */
    }
  }

  /* ── Cleaning up after a refusal ────────────────────────────── */

  /* The key map comes from the markup — the same place as the cookie
     declaration. Keeping it here would create a second list that drifts
     apart from the first over time. */
  function purgeMap() {
    var raw = root.getAttribute('data-bzc-purge');
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  function purge(next) {
    var map = purgeMap();

    CATS.forEach(function (cat) {
      if (next[cat]) return;

      var keys = map[cat];
      if (!Array.isArray(keys)) return;

      keys.forEach(function (key) {
        try {
          localStorage.removeItem(key);
        } catch (e) {}
      });
    });
  }

  /* ── Shopify Customer Privacy API ───────────────────────────── */

  function withApi(fn) {
    var S = window.Shopify;
    if (!S) return;
    if (S.customerPrivacy) return fn(S.customerPrivacy);
    if (typeof S.loadFeatures !== 'function') return;

    S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      if (err || !window.Shopify.customerPrivacy) return;
      fn(window.Shopify.customerPrivacy);
    });
  }

  function want(next) {
    return {
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      preferences: !!next.preferences,
      /* Sale of data is kept behind marketing: the Norwegian layout has
         no separate category for it, and leaving the field unset means
         leaving it to Shopify's discretion. */
      sale_of_data: !!next.marketing
    };
  }

  function pushToShopify(next) {
    withApi(function (api) {
      try {
        api.setTrackingConsent(want(next), function () {});
      } catch (e) {
        /* Consent is already recorded on our side; we will retry on the next load. */
      }
    });
  }

  /* A quiet reconciliation on every load. Not for tidiness: Shopify's
     cookie lives its own life and can vanish independently of ours, and
     then the pixels would silently stop seeing consent. */
  function syncToShopify(next) {
    withApi(function (api) {
      var cur = {};
      try {
        cur = api.currentVisitorConsent() || {};
      } catch (e) {
        cur = {};
      }

      var target = want(next);
      var stale = false;

      for (var key in target) {
        if (!Object.prototype.hasOwnProperty.call(target, key)) continue;
        var v = cur[key];
        /* An empty string from Shopify means "never asked" — that is not
           the same as "refused", and it needs a record. */
        if (v !== 'yes' && v !== 'no') { stale = true; break; }
        if ((v === 'yes') !== target[key]) { stale = true; break; }
      }

      if (!stale) return;

      try {
        api.setTrackingConsent(target, function () {});
      } catch (e) {}
    });
  }

  /* ── Panel and drawer ───────────────────────────────────────── */

  function showBar() {
    // Never in the theme editor's preview — see snippets/breakz-consent.
    if (window.Shopify && window.Shopify.designMode) return;
    if (bar) bar.hidden = false;
  }

  /* What the person used last: a key or a pointer. :focus-visible alone is
     not enough — one engine counted a click on the button as keyboard focus
     and the skip link popped up after a mouse answer (18.09.2026). */
  var lastInputKeyboard = false;
  document.addEventListener('keydown', function () { lastInputKeyboard = true; }, true);
  document.addEventListener('pointerdown', function () { lastInputKeyboard = false; }, true);

  function hideBar() {
    if (!bar) return;
    /* The panel is the last thing in the document. Hidden with focus inside
       it, it dropped focus to <body> at the end of the page, and the next Tab
       went nowhere (keyboard walk, 18.09.2026). When the choice was made from
       the keyboard, focus goes to the top of the page instead — the skip
       link, which is the next stop there anyway. After a mouse click nothing
       moves: a mouse user would only see the skip link pop up. */
    var active = document.activeElement;
    var fromKeyboard = false;
    try {
      fromKeyboard = lastInputKeyboard && !!active && bar.contains(active) && active.matches(':focus-visible');
    } catch (e) {}
    bar.hidden = true;
    if (fromKeyboard) {
      var start = document.querySelector('.skip-to-content-link');
      if (start && start.focus) start.focus();
    }
  }

  function fillSwitches(from) {
    CATS.forEach(function (cat) {
      var input = modal.querySelector('[data-bzc-cat="' + cat + '"]');
      /* Nothing is pre-ticked — a direct requirement of brief §11.
         With no record all three are off; with a record we show exactly
         what the person chose last time. */
      if (input) input.checked = from ? !!from[cat] : false;
    });
  }

  function openModal(highlight, note) {
    if (!modal) return;

    opener = document.activeElement;
    fillSwitches(state);

    if (ask) {
      if (note) {
        if (askText) askText.textContent = note;
        ask.hidden = false;
      } else {
        ask.hidden = true;
      }
    }

    if (highlight) {
      var row = modal.querySelector('[data-bzc-row="' + highlight + '"]');
      if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    }

    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
  }

  function closeModal() {
    if (!modal) return;
    if (typeof modal.close === 'function') {
      if (modal.open) modal.close();
    } else {
      modal.removeAttribute('open');
    }
  }

  function readSwitches() {
    var next = {};
    CATS.forEach(function (cat) {
      var input = modal.querySelector('[data-bzc-cat="' + cat + '"]');
      next[cat] = !!(input && input.checked);
    });
    return next;
  }

  function all(value) {
    return { preferences: value, analytics: value, marketing: value };
  }

  /* ── Decision ───────────────────────────────────────────────── */

  function decide(next, source) {
    state = {
      preferences: !!next.preferences,
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      at: 0
    };

    writeRecord(state, source);
    pushToShopify(state);
    purge(state);

    hideBar();
    closeModal();
    broadcast();
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent('breakz:consent', {
      detail: {
        necessary: true,
        preferences: state.preferences,
        analytics: state.analytics,
        marketing: state.marketing
      }
    }));
  }

  function settlePending() {
    if (!pending) return;
    var resolve = pending.resolve;
    var cat = pending.cat;
    pending = null;
    resolve(!!(state && state[cat]));
  }

  /* ── Events ─────────────────────────────────────────────────── */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-bzc-open]')) {
      event.preventDefault();
      openModal(null, null);
      return;
    }
    if (target.closest('[data-bzc-accept]')) {
      decide(all(true), 'banner:accept-all');
      return;
    }
    if (target.closest('[data-bzc-reject]')) {
      decide(all(false), 'banner:reject-all');
      return;
    }
    if (target.closest('[data-bzc-modal-accept]')) {
      decide(all(true), 'modal:accept-all');
      return;
    }
    if (target.closest('[data-bzc-modal-reject]')) {
      decide(all(false), 'modal:reject-all');
      return;
    }
    if (target.closest('[data-bzc-save]')) {
      decide(readSwitches(), 'modal:save');
      return;
    }
    if (target.closest('[data-bzc-close]')) {
      closeModal();
    }
  });

  if (modal) {
    modal.addEventListener('close', function () {
      settlePending();
      if (opener && opener.focus) opener.focus();
      opener = null;
    });

    /* The drawer is a form. Enter inside it must not submit anything
       around the buttons: a submit without a button would mean a
       decision the person never made. */
    var form = modal.querySelector('form');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
      });
    }
  }

  /* If consent is collected by someone else — Shopify's built-in banner
     left enabled, or the checkout page — our record must not drift away
     from reality. */
  document.addEventListener('visitorConsentCollected', function (event) {
    var d = event.detail || {};
    if (!d || typeof d.analyticsAllowed === 'undefined') return;

    var next = {
      preferences: !!d.preferencesAllowed,
      analytics: !!d.analyticsAllowed,
      marketing: !!d.marketingAllowed
    };

    var same = state &&
      state.preferences === next.preferences &&
      state.analytics === next.analytics &&
      state.marketing === next.marketing;

    if (same) return;

    state = { preferences: next.preferences, analytics: next.analytics, marketing: next.marketing, at: 0 };
    writeRecord(state, 'shopify:visitorConsentCollected');
    purge(state);
    hideBar();
    broadcast();
  });

  /* ── Public surface for the rest of the theme ───────────────── */

  window.BreakzConsent = {
    get: function () {
      return {
        necessary: true,
        preferences: !!(state && state.preferences),
        analytics: !!(state && state.analytics),
        marketing: !!(state && state.marketing),
        decided: !!state
      };
    },

    allows: function (cat) {
      if (cat === 'necessary') return true;
      return !!(state && state[cat]);
    },

    open: function (highlight, note) {
      openModal(highlight || null, note || null);
    },

    /* Ask for one thing and wait for the answer. That is how the Twitch
       player gets permission at the moment it is needed, rather than up
       front on the first screen. */
    request: function (cat, note) {
      var self = this;
      return new Promise(function (resolve) {
        if (self.allows(cat)) return resolve(true);
        if (pending) pending.resolve(false);
        pending = { cat: cat, resolve: resolve };
        openModal(cat, note || null);
      });
    },

    records: function () {
      try {
        var log = JSON.parse(localStorage.getItem(LOG_KEY) || '[]');
        return Array.isArray(log) ? log : [];
      } catch (e) {
        return [];
      }
    }
  };

  /* ── Start ──────────────────────────────────────────────────── */

  /* Two URL keys. By design the panel is one-shot: answer it and it does
     not come back for a year. For checking the layout that is awkward,
     and incognito only helps until you close the window.

     `?bzc=reset` — erase your choice and show the panel again.
     `?bzc=open`  — open the settings drawer straight away.

     The keys stay in production, and that is deliberate: they allow and
     forbid nothing, they only bring a person back to the question.
     Support has something to send a shopper who wants to change their
     mind, and the link can be bookmarked.

     We read both the query string and the hash. The hash is not a luxury
     here: a password-protected store redirects `/` to `/password` and
     loses the query string on the way — verified, `?bzc=reset` never
     reaches the script. The browser carries the hash to the redirect
     target itself, because it is never sent to the server at all. */
  function readForce() {
    try {
      var v = new URLSearchParams(location.search).get('bzc');
      if (!v && location.hash.length > 1) {
        v = new URLSearchParams(location.hash.slice(1)).get('bzc');
      }
      return v;
    } catch (e) {
      return null;
    }
  }

  function clearRecord() {
    document.cookie =
      COOKIE + '=; path=/; max-age=0; samesite=lax' +
      (location.protocol === 'https:' ? '; secure' : '');
  }

  /* The key must work without a reload too. Changing only the hash is
     navigation within the same document: the browser does not reload the
     page and does not run the script again. Someone already on the site
     would paste the link into the same tab and see nothing happen.
     Caught by measurement, not by reasoning. */
  function applyForce(v) {
    if (v === 'reset') {
      clearRecord();
      state = null;
      purge({ preferences: false, analytics: false, marketing: false });
      closeModal();
      showBar();
      return;
    }
    if (v === 'open') openModal(null, null);
  }

  window.addEventListener('hashchange', function () {
    applyForce(readForce());
  });

  var force = readForce();

  if (force === 'reset') clearRecord();

  state = readRecord();

  if (state) {
    syncToShopify(state);
    purge(state);
  } else {
    /* No record means no permission. Anything left over from earlier
       consent or from an expired year is cleared before the person
       answers. */
    purge({ preferences: false, analytics: false, marketing: false });
    showBar();
  }

  if (force === 'open') openModal(null, null);

  /* The snippet shows the panel before this script runs and remembers a
     tap that landed in between. Replay it now that the handlers exist. */
  var earlyTap = window.BreakzConsentTap;
  window.BreakzConsentTap = null;
  if (earlyTap && earlyTap.isConnected) earlyTap.click();
})();
